import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    forwardRef,
    inject
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Basket as NBasket } from '@df/basket/basket.interfaces';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { AbModule } from '@df/module-ab/ab.module';
import { ImageModule } from '@df/module-image/image.module';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/api/customer';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { LastCategoryPathPipe } from '@df/ui/common/last-category-path.pipe';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs';
import { FurnitureAssemblyComponent } from '../../module/furniture-assembly/furniture-assembly.component';
import { PaypalPayInThreeMessageComponent } from '../../module/paypal-pay-in-three/paypal-pay-in-three-message.component';
import { PaypalExpressButtonComponent } from '../../module/paypal/paypal-express-button.component';
import { ProtectionPlansComponent } from '../../module/protection-plans/protection-plans.component';
import { FinanceFromBasketComponent } from '../../module/v12-retail-finance/from/basket/v12-retail-finance-from-basket.component';
import { CheckboxComponent } from '../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { GoBackButtonComponent } from '../../ui/go-back-button/go-back-button.component';
import { LocalStickyPanelDirective } from '../../ui/sticky/sticky-panel.directive';
import { BasketFreeDeliveryNoticeComponent } from '../free-shipping/basket-free-shipping.component';
import { BasketViewCouponFormComponent } from '../view-coupon-form/basket-view-coupon-form.component';
import { BasketViewItemComponent } from '../view-item/basket-view-item.component';
import { BasketViewPayWithComponent } from '../view-paywith/basket-view-pay-with.component';
import { BasketViewSubmitComponent } from '../view-submit/basket-view-submit.component';
import { BasketViewTotalsComponent } from '../view-totals/basket-view-totals.component';
import { NBasketViewCms } from './basket-view-cms.component';

@Component({
    selector: 'basket-view',
    templateUrl: './basket-view.html',
    styleUrls: ['../view-item/basket-view-item.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormsModule,
        CommonModule,
        ICmsClientModule,
        TranslationModule,
        LastCategoryPathPipe,
        RouterLink,
        ImageModule,
        BasketViewCouponFormComponent,
        PaypalExpressButtonComponent,
        BasketViewPayWithComponent,
        FormsModule,
        BasketViewSubmitComponent,
        ProtectionPlansComponent,
        FinanceFromBasketComponent,
        LocalStickyPanelDirective,
        forwardRef(() => BasketViewTotalsComponent),
        FurnitureAssemblyComponent,
        InputWrapComponent,
        BasketViewItemComponent,
        BasketFreeDeliveryNoticeComponent,
        GoBackButtonComponent,
        OrderByPipe,
        IfSizeDirective,
        AbModule,
        InputWrapBodyDirective,
        CheckboxComponent,
        PaypalPayInThreeMessageComponent
    ]
})
export class BasketViewComponent implements OnDestroy, OnInit {
    protected subscriptions: Subscription = new Subscription();
    protectionPlanAgreements: { [key: number]: boolean } = {};

    @ViewChild('form', { static: false })
    form?: NgForm;

    @Input()
    content?: NBasketViewCms.IBlockDataContent;

    private events = inject(Events);

    isInStock: boolean = false;
    isSalable: boolean = false;
    isVirtual: boolean = false;
    basketSubscription?: Subscription;

    get basket() {
        return this.customer.basket;
    }

    constructor(
        protected _ref: ChangeDetectorRef,
        readonly app: App,
        private customer: Customer,
        private dfEventService: DfEventService
    ) {
        this.onCustomerUpdate();
    }

    ngOnInit() {
        this.sendBasketViewEvent();

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === 'basketDataChange') {
                    this.onCustomerUpdate();
                    this._ref.detectChanges();
                }
            })
        );
        this.app.ab.getSubject('expressButtons').subscribe(() => {
            this.detectChanges();
        });
    }

    ngOnDestroy() {
        if (this.basketSubscription) {
            this.basketSubscription.unsubscribe();
        }

        this.subscriptions.unsubscribe();
    }

    protected onCustomerUpdate() {
        this.isSalable = this.basket.isSalable;
        this.isInStock = this.basket.isInStock;
        this.isVirtual = this.basket.isVirtual;
    }

    trackByItemId(index: number, item: BasketItem) {
        return item.id;
    }

    protected sendBasketViewEvent() {
        this.events.broadcast(NBasket.EVENTS.view);
    }

    itemTrackFn(index: number, item: BasketItem): number {
        return item.id;
    }

    openPdf(file: string): void {
        window.open(file, '_blank');
    }

    detectChanges(): void {
        this._ref.detectChanges();
    }
}
